import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Clock, CheckCircle, Truck, XCircle, 
  ChevronDown, ChevronUp, Filter, Search,
  Phone, Mail, MapPin, Calendar, RefreshCcw, 
  ArrowRight, AlertTriangle, DollarSign, Loader2,
  CreditCard, Smartphone, MessageCircle, Eye, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Pending' },
  awaiting_verification: { icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Awaiting Verification' },
  confirmed: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Confirmed' },
  processing: { icon: Package, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Processing' },
  shipped: { icon: Truck, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Cancelled' }
};

const paymentStatusConfig = {
  pending: { color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending' },
  pay_on_delivery: { color: 'text-blue-600', bg: 'bg-blue-50', label: 'Cash on Delivery' },
  awaiting_verification: { color: 'text-orange-600', bg: 'bg-orange-50', label: 'Awaiting Verification' },
  paid: { color: 'text-green-600', bg: 'bg-green-50', label: 'Paid' },
  failed: { color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
  refunded: { color: 'text-purple-600', bg: 'bg-purple-50', label: 'Refunded' }
};

const paymentMethodConfig = {
  pod: { icon: Truck, label: 'Cash on Delivery' },
  upi: { icon: Smartphone, label: 'UPI / GPay' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp' },
  stripe: { icon: CreditCard, label: 'Card Payment' }
};

// Strict status flow transitions
const STATUS_TRANSITIONS = {
  pending: ['confirmed', 'cancelled'],
  awaiting_verification: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing: ['shipped', 'cancelled'],
  shipped: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: []
};

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(null);
  const [refundingOrder, setRefundingOrder] = useState(null);
  const [showPaymentProof, setShowPaymentProof] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(null);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, getAuthHeaders());
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const verifyPayment = async (orderId, action) => {
    setVerifyingPayment(orderId);
    try {
      await axios.post(`${API}/orders/${orderId}/verify-payment`, { action }, getAuthHeaders());
      toast.success(action === 'verify' ? 'Payment verified!' : 'Payment rejected');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment verification');
    } finally {
      setVerifyingPayment(null);
    }
  };

  const getNextStatus = (currentStatus) => {
    const transitions = STATUS_TRANSITIONS[currentStatus] || [];
    // Return the first non-cancel transition (the "next" status in flow)
    return transitions.find(s => s !== 'cancelled') || null;
  };

  const canTransitionTo = (currentStatus, targetStatus) => {
    const allowed = STATUS_TRANSITIONS[currentStatus] || [];
    return allowed.includes(targetStatus);
  };

  const isFinalStatus = (status) => {
    return status === 'delivered' || status === 'cancelled';
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, order_status: newStatus } : order
      ));
      
      toast.success(`Order status updated to ${statusConfig[newStatus].label}`);
      setShowCancelDialog(null);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error updating order status:', error);
      const message = error.response?.data?.detail || 'Failed to update order status';
      toast.error(message);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const processRefund = async (orderId) => {
    setRefundingOrder(orderId);
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/refund`,
        { reason: "Admin initiated refund" },
        getAuthHeaders()
      );
      
      // Update order in state with refund status
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, refund_status: 'refunded' } : order
      ));
      
      toast.success(response.data.message || 'Refund processed successfully');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error processing refund:', error);
      const message = error.response?.data?.detail || 'Failed to process refund';
      toast.error(message);
    } finally {
      setRefundingOrder(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || statusConfig.pending;
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.order_status === statusFilter;
    const matchesSearch = 
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.order_status === 'pending').length,
    processing: orders.filter(o => ['confirmed', 'processing'].includes(o.order_status)).length,
    shipped: orders.filter(o => o.order_status === 'shipped').length,
    delivered: orders.filter(o => o.order_status === 'delivered').length,
    cancelled: orders.filter(o => o.order_status === 'cancelled').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0047AB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          Order Management
        </h1>
        <Button onClick={fetchOrders} variant="outline" size="sm">
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-900">{orderStats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-700">Pending</p>
          <p className="text-2xl font-bold text-yellow-800">{orderStats.pending}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-sm text-purple-700">Processing</p>
          <p className="text-2xl font-bold text-purple-800">{orderStats.processing}</p>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
          <p className="text-sm text-indigo-700">Shipped</p>
          <p className="text-2xl font-bold text-indigo-800">{orderStats.shipped}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-green-700">Delivered</p>
          <p className="text-2xl font-bold text-green-800">{orderStats.delivered}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <p className="text-sm text-red-700">Cancelled</p>
          <p className="text-2xl font-bold text-red-800">{orderStats.cancelled}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by customer name, email, or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="order-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="awaiting_verification">Awaiting Verification</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center border border-slate-200">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No orders found</p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const statusInfo = getStatusInfo(order.order_status);
            const StatusIcon = statusInfo.icon;
            const isExpanded = expandedOrder === order.id;
            const nextStatus = getNextStatus(order.order_status);
            const isFinal = isFinalStatus(order.order_status);
            const needsRefund = order.order_status === 'cancelled' && 
                               order.payment_status === 'paid' && 
                               order.refund_status !== 'refunded' &&
                               order.refund_status !== 'refunded_manual';

            return (
              <div
                key={order.id}
                className={`bg-white rounded-lg border ${statusInfo.border} overflow-hidden`}
                data-testid={`order-row-${order.id}`}
              >
                {/* Order Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${statusInfo.bg}`}>
                        <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{order.customer_name}</p>
                        <p className="text-sm text-slate-500">#{order.id}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{formatPrice(order.total_amount)}</p>
                        <p className="text-xs text-slate-500">{order.items?.length || 0} items</p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Payment Method Badge */}
                        {order.payment_method && paymentMethodConfig[order.payment_method] && (
                          <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600 flex items-center gap-1">
                            {(() => {
                              const PMIcon = paymentMethodConfig[order.payment_method].icon;
                              return <PMIcon className="w-3 h-3" />;
                            })()}
                            {paymentMethodConfig[order.payment_method].label}
                          </span>
                        )}
                        
                        {/* Order Status Badge */}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        
                        {/* Payment Status Badge */}
                        {(() => {
                          const paymentInfo = paymentStatusConfig[order.payment_status] || paymentStatusConfig.pending;
                          return (
                            <span className={`px-2 py-1 rounded-full text-xs ${paymentInfo.bg} ${paymentInfo.color}`}>
                              {paymentInfo.label}
                            </span>
                          );
                        })()}
                        
                        {order.refund_status === 'refunded' && (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                            Refunded
                          </span>
                        )}
                      </div>

                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customer Info */}
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3">Customer Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4" />
                            {order.customer_email}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4" />
                            {order.customer_phone}
                          </div>
                          <div className="flex items-start gap-2 text-slate-600">
                            <MapPin className="w-4 h-4 mt-0.5" />
                            <span>{order.delivery_address}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Calendar className="w-4 h-4" />
                            {formatDate(order.created_at)}
                          </div>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3">Order Items</h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-slate-600">
                                {item.product_name} × {item.quantity}
                              </span>
                              <span className="font-medium text-slate-900">
                                {formatPrice(item.price * item.quantity)}
                              </span>
                            </div>
                          ))}
                          <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold">
                            <span>Total</span>
                            <span>{formatPrice(order.total_amount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cancellation Info */}
                    {order.order_status === 'cancelled' && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-red-700">Order Cancelled</p>
                            <p className="text-sm text-red-600">
                              Cancelled by {order.cancelled_by || 'system'} 
                              {order.cancelled_at && ` on ${formatDate(order.cancelled_at)}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Verification Section for UPI Orders */}
                    {order.payment_status === 'awaiting_verification' && (
                      <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium text-orange-800 flex items-center gap-2">
                              <Clock className="w-5 h-5" />
                              Payment Awaiting Verification
                            </p>
                            <p className="text-sm text-orange-700 mt-1">
                              Customer paid via UPI. Please verify the payment before confirming the order.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {order.payment_proof && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowPaymentProof(order)}
                                className="border-orange-300 text-orange-700 hover:bg-orange-100"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Proof
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => verifyPayment(order.id, 'verify')}
                              disabled={verifyingPayment === order.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {verifyingPayment === order.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Verify
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyPayment(order.id, 'reject')}
                              disabled={verifyingPayment === order.id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Status Update Actions */}
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-3">Order Actions</h4>
                      
                      {isFinal ? (
                        <div className="flex items-center gap-4">
                          <p className="text-sm text-slate-500">
                            This order is {order.order_status}. No further status changes allowed.
                          </p>
                          
                          {/* Refund Button for Cancelled & Paid Orders */}
                          {needsRefund && (
                            <Button
                              onClick={() => processRefund(order.id)}
                              disabled={refundingOrder === order.id}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`refund-btn-${order.id}`}
                            >
                              {refundingOrder === order.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <DollarSign className="w-4 h-4 mr-2" />
                                  Process Refund
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-3">
                          {/* Current Status */}
                          <div className={`px-4 py-2 rounded-lg text-sm font-medium ${statusInfo.bg} ${statusInfo.color} border ${statusInfo.border}`}>
                            Current: {statusInfo.label}
                          </div>
                          
                          {/* Next Status Button */}
                          {nextStatus && (
                            <>
                              <ArrowRight className="w-5 h-5 text-slate-400" />
                              <Button
                                onClick={() => updateOrderStatus(order.id, nextStatus)}
                                disabled={updatingStatus === order.id}
                                className="bg-[#0047AB] hover:bg-[#003380]"
                                data-testid={`next-status-btn-${order.id}`}
                              >
                                {updatingStatus === order.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : null}
                                Move to {statusConfig[nextStatus].label}
                              </Button>
                            </>
                          )}
                          
                          {/* Cancel Button */}
                          <Button
                            variant="outline"
                            onClick={() => setShowCancelDialog(order.id)}
                            disabled={updatingStatus === order.id}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`cancel-btn-${order.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancel Order
                          </Button>
                        </div>
                      )}
                      
                      {/* Status Flow Indicator */}
                      {!isFinal && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                          <span>Flow:</span>
                          {['pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((status, idx, arr) => (
                            <span key={status} className="flex items-center gap-1">
                              <span className={order.order_status === status ? 'font-bold text-[#0047AB]' : ''}>
                                {statusConfig[status].label}
                              </span>
                              {idx < arr.length - 1 && <ArrowRight className="w-3 h-3" />}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!showCancelDialog} onOpenChange={() => setShowCancelDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancel Order?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
              {orders.find(o => o.id === showCancelDialog)?.payment_status === 'paid' && (
                <span className="block mt-2 text-amber-600 font-medium">
                  Note: You will need to process a refund after cancellation.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelDialog(null)}>
              Keep Order
            </Button>
            <Button 
              onClick={() => updateOrderStatus(showCancelDialog, 'cancelled')}
              disabled={updatingStatus === showCancelDialog}
              className="bg-red-600 hover:bg-red-700"
            >
              {updatingStatus === showCancelDialog ? 'Cancelling...' : 'Yes, Cancel Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Proof Dialog */}
      <Dialog open={!!showPaymentProof} onOpenChange={() => setShowPaymentProof(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Payment Proof - #{showPaymentProof?.id}
            </DialogTitle>
          </DialogHeader>
          {showPaymentProof?.payment_proof ? (
            <div className="space-y-4">
              <img 
                src={showPaymentProof.payment_proof} 
                alt="Payment Proof" 
                className="w-full rounded-lg border border-slate-200"
              />
              <p className="text-sm text-slate-500 text-center">
                Uploaded on {showPaymentProof.payment_proof_uploaded_at ? formatDate(showPaymentProof.payment_proof_uploaded_at) : 'N/A'}
              </p>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No payment proof uploaded</p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPaymentProof(null)}>
              Close
            </Button>
            <Button 
              onClick={() => { verifyPayment(showPaymentProof.id, 'verify'); setShowPaymentProof(null); }}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Verify Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrdersPage;
